import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';

// components
import NavbarButton from './NavbarButton';
import { KaneaLogo } from '../Common/KaneaLogo';
import { Bot } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { API_URL } from '../../config';
import { partnerService } from '../../services/partnerService';
import { adminMessagesService } from '../../services/adminMessagesService';

// styles
import './Navbar.css';

// Store scroll position and expand states outside component to persist across re-renders
let savedScrollPosition = 0;
let savedExpandState = {
  insideMarket: true,
  virtualAccount: true,
  settings: false,
  administration: false,
  tenantAdmin: false,
  alertes: true,
  ia: true,
};

/** Exercice projeté : l'année civile en cours, comme le calcul hebdomadaire du cache
 *  (tasks.run_projection_cache_calculation). Africa/Abidjan = UTC. */
const anneeProjections = new Date().getUTCFullYear();

const Navbar: React.FC = () => {
  const { hasPermission, logout, user, token } = useAuth();
  const { isSidebarCollapsed, toggleSidebar, closeMobileMenu } = useLayout();
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // Auto-close mobile drawer when a link is clicked
  const handleNavClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a') && window.innerWidth < 768) {
      closeMobileMenu();
    }
  };

  // State for collapsible sections - initialize from persisted values
  const [isInsideMarketOpen, setIsInsideMarketOpen] = useState(savedExpandState.insideMarket);
  const [isVirtualAccountOpen, setIsVirtualAccountOpen] = useState(savedExpandState.virtualAccount);
  const [isSettingsOpen, setIsSettingsOpen] = useState(savedExpandState.settings);
  const [isAdministrationOpen, setIsAdministrationOpen] = useState(savedExpandState.administration);
  const [isTenantAdminOpen, setIsTenantAdminOpen] = useState(savedExpandState.tenantAdmin);
  const [isAlertesOpen, setIsAlertesOpen] = useState(savedExpandState.alertes);
  const [isIAOpen, setIsIAOpen] = useState(savedExpandState.ia);
  const [unreadAdminMessages, setUnreadAdminMessages] = useState<number>(0);
  const [isPartner, setIsPartner] = useState(false);

  // Poll for unread admin messages
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (user && hasPermission('admin_platform') && token) {
      const fetchUnreadCount = async () => {
        try {
          // Onglet en arrière-plan : inutile d'interroger l'API chaque minute.
          if (document.hidden) return;
          const nonLus = await adminMessagesService.getUnreadCount(token);
          if (nonLus !== null) setUnreadAdminMessages(nonLus);
        } catch (error) {
          console.error('Error fetching unread messages count:', error);
        }
      };

      fetchUnreadCount();
      interval = setInterval(fetchUnreadCount, 60000); // 1-minute interval
    }
    return () => clearInterval(interval);
  }, [user, hasPermission, token]);

  // Check if user is a partner
  useEffect(() => {
    if (user && token) {
      partnerService.getStats()
        .then(stats => setIsPartner(stats.is_partner))
        .catch(() => setIsPartner(false));
    }
  }, [user, token]);

  // Persist expand states when they change
  useEffect(() => {
    savedExpandState = {
      insideMarket: isInsideMarketOpen,
      virtualAccount: isVirtualAccountOpen,
      settings: isSettingsOpen,
      administration: isAdministrationOpen,
      tenantAdmin: isTenantAdminOpen,
      alertes: isAlertesOpen,
      ia: isIAOpen,
    };
  }, [isInsideMarketOpen, isVirtualAccountOpen, isSettingsOpen, isAdministrationOpen, isTenantAdminOpen, isAlertesOpen, isIAOpen]);

  // Restore scroll position after route change
  useEffect(() => {
    if (navRef.current && savedScrollPosition > 0) {
      navRef.current.scrollTop = savedScrollPosition;
    }
  }, [location.pathname]);

  // Save scroll position on scroll
  const handleScroll = () => {
    if (navRef.current) {
      savedScrollPosition = navRef.current.scrollTop;
    }
  };

  return (
    <nav ref={navRef} className={`navbar-inner no-select ${isSidebarCollapsed ? 'collapsed' : ''}`} onScroll={handleScroll} onClick={handleNavClick}>
      <div className='sidebar-controls'>
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">
          <i className="material-icons">{isSidebarCollapsed ? 'menu' : 'menu_open'}</i>
        </button>
      </div>

      <div className='logo'>
        <Link to='/dashboard'>
          <KaneaLogo
            variant={isSidebarCollapsed ? 'mark' : 'horizontal'}
            title='KaneaBourse — aller au tableau de bord'
            markColor='#6C3FC4'
            height={isSidebarCollapsed ? 32 : 30}
            style={{
              transition: 'all 0.3s ease',
            }}
          />
        </Link>
      </div>

      {/* Zone IA */}
      <h3 onClick={() => setIsIAOpen(!isIAOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '15px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className="material-icons" style={{ fontSize: '20px' }}>psychology</i>
          Analyste IA
        </span>
        <i className="material-icons" style={{ fontSize: '18px' }}>{isIAOpen ? 'expand_less' : 'expand_more'}</i>
      </h3>
      {isIAOpen && (
        <ul>
          <li>
            <NavbarButton
              url='/chat'
              icon={<Bot size={20} />}
              title='Discuter avec Nia'
              tooltip="Votre analyste financière IA disponible 24h/24."
              badgeColor='#22c55e'
              dot={true}
            />
          </li>
          <li>
            <NavbarButton url='/nia-portfolio' icon='smart_toy' title={hasPermission('view_advanced_ai_data') ? 'Trading IA ✨' : 'Trading IA'} />
          </li>
          <li>
            <NavbarButton url='/ai-watchlist' icon='auto_awesome' title={hasPermission('view_watchlist_ia') ? 'Watchlist IA ✨' : 'Watchlist IA'} />
          </li>
        </ul>
      )}

      {/* Le Marché – données marché BRVM */}
      {hasPermission('view_market_data') && (
        <>
          <h3 onClick={() => setIsInsideMarketOpen(!isInsideMarketOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '8px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="material-icons" style={{ fontSize: '20px' }}>insights</i>
              Le Marché
            </span>
            <i className="material-icons" style={{ fontSize: '18px' }}>{isInsideMarketOpen ? 'expand_less' : 'expand_more'}</i>
          </h3>
          {isInsideMarketOpen && (
            <ul>
              <li>
                <NavbarButton url='/dashboard' icon='dashboard' title='Tableau de bord' />
              </li>
              <li>
                <NavbarButton url='/market/briefing' icon='mail' title='Flash Marché' />
              </li>
              <li>
                <NavbarButton
                  url='/analysis'
                  icon='insights'
                  title={hasPermission('view_advanced_data') ? 'Analyse Globale' : 'Analyse Globale 🔒'}
                />
              </li>
              <li>
                <NavbarButton url='/cotations' icon='show_chart' title='Marché des actions' />
              </li>
              <li>
                <NavbarButton url='/data' icon='analytics' title='Terminal' />
              </li>
              <li>
                <NavbarButton url='/calendar' icon='event' title='Calendrier' />
              </li>
              <li>
                <NavbarButton
                  url='/resultats'
                  icon='assessment'
                  title={hasPermission('view_advanced_data') ? 'Résultats des sociétés' : 'Résultats des sociétés 🔒'}
                />
              </li>
              <li>
                <NavbarButton url='/favorites' icon='star' title='Mes favoris' />
              </li>
              <li>
                <NavbarButton url='/notes-taches' icon='assignment' title='Notes & Tâches' />
              </li>
              <li>
                <NavbarButton url='/reports' icon='description' title='Publications Kanea' />
              </li>
              {hasPermission('admin_platform') && (
                <li>
                  <NavbarButton url='/publications' icon='auto_awesome' title='Analyses IA' />
                </li>
              )}
              {(hasPermission('admin_platform') || hasPermission('trader_experimental')) && (
                <>
                  <li>
                    <NavbarButton url='/admin/convergence-radar' icon='track_changes' title='Radar de Convergence' />
                  </li>
                  <li>
                    <NavbarButton url='/valuation' icon='filter_alt' title='Screener Valorisation' />
                  </li>
                  <li>
                    <NavbarButton url='/financial-projections' icon='trending_up' title={`Projections ${anneeProjections}`} />
                  </li>
                </>
              )}
            </ul>
          )}
        </>
      )}

      {/* Alertes & Signaux */}
      {hasPermission('view_market_data') && (
        <>
          <h3 onClick={() => setIsAlertesOpen(!isAlertesOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '8px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="material-icons" style={{ fontSize: '20px' }}>notifications_active</i>
              Alertes &amp; Signaux
            </span>
            <i className="material-icons" style={{ fontSize: '18px' }}>{isAlertesOpen ? 'expand_less' : 'expand_more'}</i>
          </h3>
          {isAlertesOpen && (
            <ul>
              <li>
                <NavbarButton url='/settings/alerts' icon='notifications' title='Mes Alertes Prix' />
              </li>
              <li>
                <NavbarButton
                  url='/technical-alerts'
                  icon='notifications_active'
                  title={hasPermission('alertes_pro') ? 'Signaux Pro ✨' : 'Signaux Pro 🔒'}
                />
              </li>
            </ul>
          )}
        </>
      )}

      {/* Virtual Account Section - requires manage_portfolio */}
      {
        hasPermission('manage_portfolio') && (
          <>
            <h3 onClick={() => setIsVirtualAccountOpen(!isVirtualAccountOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '8px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="material-icons" style={{ fontSize: '20px' }}>account_balance_wallet</i>
                Mon Portefeuille
              </span>
              <i className="material-icons" style={{ fontSize: '18px' }}>{isVirtualAccountOpen ? 'expand_less' : 'expand_more'}</i>
            </h3>
            {isVirtualAccountOpen && (
              <ul>
                {/* 1. Gestion du Cash (Wallet) */}
                <li>
                  <NavbarButton url='/wallet' icon='account_balance_wallet' title='Ma Caisse (Cash)' />
                </li>

                {/* 2. Suivi des Actions (Holdings) */}
                <li>
                  <NavbarButton url='/my-assets' icon='trending_up' title='Mes Actifs' />
                </li>

                <li>
                  <NavbarButton url='/transactions' icon='sync' title='Transactions' />
                </li>


                {/* 6. Analyse Avancée */}
                <li>
                  <NavbarButton
                    url='/portfolio-analysis'
                    icon='pie_chart'
                    title={hasPermission('view_advanced_data') ? 'Analyse Portefeuille' : 'Analyse Portefeuille 🔒'}
                  />
                </li>

                {/* 7. Social / Compétition */}
                <li>
                  <NavbarButton url='/leaderboard' icon='emoji_events' title='Leaderboard' />
                </li>

                {/* Stratégies de Trading (Abonnés) */}
                {hasPermission('trader_value') && (
                  <li>
                    <NavbarButton url='/rebalancing' icon='balance' title='Rééquilibrage Intelligent' />
                  </li>
                )}
                {hasPermission('trader_prudent') && (
                  <li>
                    <NavbarButton url='/marketconfig/prudent' icon='verified_user' title='Investisseur Prudent' />
                  </li>
                )}
                {hasPermission('trader_pepites') && (
                  <li>
                    <NavbarButton url='/marketconfig/pepites' icon='diamond' title='Chasseur de Pépites' />
                  </li>
                )}
                {hasPermission('trader_alpha_fusion') && (
                  <li>
                    <NavbarButton url='/marketconfig/alpha-fusion' icon='rocket_launch' title='Kanea Alpha Fusion' />
                  </li>
                )}
              </ul>
            )}
          </>
        )
      }

      {/* Settings Section */}
      <h3 onClick={() => setIsSettingsOpen(!isSettingsOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '15px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className="material-icons" style={{ fontSize: '20px' }}>settings</i>
          Paramètres
        </span>
        <i className="material-icons" style={{ fontSize: '18px' }}>{isSettingsOpen ? 'expand_less' : 'expand_more'}</i>
      </h3>
      {
        isSettingsOpen && (
          <ul>
            <li>
              <NavbarButton url='/members' icon='account_circle' title='Mon profil' />
            </li>
            <li>
              <NavbarButton url='/sponsorship' icon='card_giftcard' title='Parrainage 🎁' />
            </li>
            {isPartner && (
              <li>
                <NavbarButton url='/settings/partner' icon='handshake' title='Espace Partenaire' />
              </li>
            )}
            <li>
              <NavbarButton url='/settings/messages' icon='email' title='Messages' />
            </li>
            <li>
              <NavbarButton url='/settings/contacts' icon='contact_mail' title='Contacts' />
            </li>
            {hasPermission('view_documentation') && (
              <li>
                <NavbarButton url='/docs' icon='menu_book' title='Documentation' />
              </li>
            )}
          </ul>
        )
      }

      {/* Administration Section */}
      {
        hasPermission('admin_platform') && (
          <>
            <h3 onClick={() => setIsAdministrationOpen(!isAdministrationOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '15px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="material-icons" style={{ fontSize: '20px' }}>admin_panel_settings</i>
                Administration
              </span>
              <i className="material-icons" style={{ fontSize: '18px' }}>{isAdministrationOpen ? 'expand_less' : 'expand_more'}</i>
            </h3>
            {isAdministrationOpen && (
              <ul>
                <li>
                  <NavbarButton url='/admin/dashboard' icon='dashboard' title='Dashboard Global' />
                </li>
                <li>
                  <NavbarButton url='/admin/nia' icon={<Bot size={20} />} title='Dashboard Nia IA ✨' />
                </li>
                <li>
                  <NavbarButton url='/admin/tenants' icon='business' title='Gestion des Tenants' />
                </li>
                <li>
                  <NavbarButton url='/admin/roles' icon='security' title='Gestion des Rôles' />
                </li>
                <li>
                  <NavbarButton url='/admin/permissions' icon='vpn_key' title='Gestion des Permissions' />
                </li>
                <li>
                  <NavbarButton url='/admin/users' icon='people' title='Utilisateurs (Global)' />
                </li>
                <li>
                  <NavbarButton url='/admin/session-anomalies' icon='gavel' title='Anomalies & Partages 🛡️' />
                </li>
                <li>
                  <NavbarButton url='/admin/subscriptions' icon='subscriptions' title='Abonnements' />
                </li>
                <li>
                  <NavbarButton url='/admin/promo-codes' icon='confirmation_number' title='Codes Promo' />
                </li>
                <li>
                  <NavbarButton url='/admin/commissions' icon='payments' title='Commissions' />
                </li>
                <li>
                  <NavbarButton url='/admin/logs' icon='history' title='Logs' />
                </li>
                <li>
                  <NavbarButton url='/admin/payments' icon='payments' title='Paiements / Plans' />
                </li>
                <li>
                  <NavbarButton url='/admin/maintenance' icon='build' title='Maintenance' />
                </li>
                <li>
                  <NavbarButton url='/admin/monitoring' icon='monitor_heart' title='Monitoring Azure' />
                </li>
                <li>
                  <NavbarButton url='/admin/azure-functions' icon='terminal' title='Azure Functions' />
                </li>
                <li>
                  <NavbarButton url='/admin/reports-audit' icon='fact_check' title='Audit Rapports' />
                </li>
                <li>
                  <NavbarButton url='/admin/holidays' icon='event_busy' title='Calendrier Boursier' />
                </li>
                <li>
                  <NavbarButton url='/admin/marche-live' icon='speed' title='Marché Live' />
                </li>
                <li>
                  <NavbarButton url='/admin/journal-imports' icon='history' title='Journal des imports' />
                </li>
                <li>
                  <NavbarButton url='/admin/operations-titres' icon='call_split' title='Opérations sur titres' />
                </li>
                {/* Legacy or specific features */}
                <li>
                  <NavbarButton url='/admin/messages' icon='mail' title='Messages Utilisateurs' badgeCount={unreadAdminMessages} />
                </li>
                <li>
                  <NavbarButton url='/admin/societes' icon='domain' title='Sociétés' />
                </li>

                <li>
                  <NavbarButton url='/admin/cotations' icon='show_chart' title='Actions' />
                </li>
                <li>
                  <NavbarButton url='/admin/discovered-reports' icon='travel_explore' title='Rapports Détectés' />
                </li>
                <li>
                  <NavbarButton url='/admin/discovered-avis' icon='notifications_active' title='Avis Détectés' />
                </li>
                <li>
                  <NavbarButton url='/admin/social-posts' icon='photo_camera' title='Publications Réseaux Sociaux' />
                </li>
                <li>
                  <NavbarButton url='/admin/proposed-articles' icon='smart_toy' title='Articles Proposés' />
                </li>
                <li>
                  <NavbarButton url='/admin/email-campaigns' icon='campaign' title='Campagnes Email' />
                </li>
                <li>
                  <NavbarButton url='/admin/dividends' icon='payments' title='Dividendes' />
                </li>
                <li>
                  <NavbarButton url='/admin/fundamentals' icon='assessment' title='📊 Fondamentales' />
                </li>
                <li>
                  <NavbarButton url='/admin/fundamentals-anomalies' icon='warning' title='⚠️ Anomalies Fondamentales' />
                </li>
                <li>
                  <NavbarButton url='/admin/leaderboard-exclusions' icon='gavel' title='Exclusions Leaderboard' />
                </li>
              </ul>
            )}
          </>
        )
      }

      {/* Tenant Admin Section */}
      {
        hasPermission('manage_tenant_settings') && (
          <>
            <h3 onClick={() => setIsTenantAdminOpen(!isTenantAdminOpen)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', marginTop: '15px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="material-icons" style={{ fontSize: '20px' }}>business</i>
                Admin Organisation
              </span>
              <i className="material-icons" style={{ fontSize: '18px' }}>{isTenantAdminOpen ? 'expand_less' : 'expand_more'}</i>
            </h3>
            {isTenantAdminOpen && (
              <ul>
                <li>
                  <NavbarButton url='/tenant/dashboard' icon='dashboard' title='Tableau de bord' />
                </li>
                <li>
                  <NavbarButton url='/tenant/users' icon='group' title='Utilisateurs' />
                </li>
                <li>
                  <NavbarButton url='/tenant/roles' icon='security' title='Rôles Locaux' />
                </li>
                <li>
                  <NavbarButton url='/tenant/settings' icon='settings' title='Paramètres' />
                </li>
                <li>
                  <NavbarButton url='/tenant/notifications' icon='notifications' title='Notifications' />
                </li>
              </ul>
            )}
          </>
        )
      }



      {/* Mobile-only: User info + Logout */}
      <div className='navbar-mobile-footer mobile-only-block'>
        <div className='navbar-mobile-user'>
          <i className='material-icons' style={{ fontSize: '32px', color: '#999' }}>account_circle</i>
          <div>
            <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-heading)' }}>
              {user?.full_name || 'Utilisateur'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {user?.email || ''}
            </div>
          </div>
        </div>
        <button
          onClick={() => { logout(); closeMobileMenu(); }}
          className='navbar-logout-btn'
        >
          <i className='material-icons'>power_settings_new</i>
          Se déconnecter
        </button>
      </div>

      <div className='copyright'>
        <strong>Kanea</strong>
        <p>
          {new Date().getFullYear()} © Tous droits réservés.
          <br />
          <br />
        </p>
      </div>
    </nav >
  );
};

export default Navbar;

